import React, { useRef, useState } from "react";
import { API } from "@/lib/api";
import { uploadFileChunked } from "@/lib/chunkedUpload";
import { toast } from "sonner";
import { Paperclip, X, Loader2, FileText } from "lucide-react";

// Sección de adjuntos (imágenes/portada, PDF, video…) para las planeaciones.
// value: array de { token, name, mime }. onChange(newArray) para persistir.
// Las imágenes se muestran como miniatura y también se incrustan en el PDF.
export default function AttachFiles({ value = [], onChange, label = "Archivos adjuntos (imagen, portada, PDF, video…)", color = "#112240", base = "attach" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const upload = async (fl) => {
    if (!fl?.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const f of fl) {
        setProgress(0);
        const data = await uploadFileChunked(f, { onProgress: setProgress });
        added.push({ token: data.token, name: data.name, mime: data.mime });
      }
      onChange([...(value || []), ...added]);
      toast.success("Archivo adjuntado");
    } catch { toast.error("No se pudo adjuntar el archivo"); }
    finally { setUploading(false); setProgress(0); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeAt = (i) => onChange((value || []).filter((_, j) => j !== i));

  return (
    <div className="p-3 rounded-lg border border-dashed border-nardo-300" data-testid={`${base}-editor`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-nardo-500 font-semibold mb-2">
        <Paperclip className="w-3 h-3" /> {label}
      </div>
      <label className="text-xs inline-flex items-center gap-1.5 text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-lg px-3 py-1.5 cursor-pointer transition-colors" style={{ "--tw-ring-color": color }} data-testid={`${base}-btn`}>
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} {uploading ? `Subiendo… ${progress}%` : "Adjuntar archivo"}
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => upload(Array.from(e.target.files || []))} />
      </label>
      {(value || []).length > 0 && (
        <div className="flex flex-wrap items-start gap-2 mt-2">
          {(value || []).map((f, i) => {
            const isImg = (f.mime || "").startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp)$/i.test(f.name || "");
            const url = `${API}/aula/archivo/${f.token}`;
            return isImg ? (
              <div key={i} className="relative group" data-testid={`${base}-file-${i}`}>
                <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={f.name} loading="lazy" className="h-20 w-20 object-cover rounded-md border border-nardo-200" data-testid={`${base}-img-${i}`} /></a>
                <button type="button" onClick={() => removeAt(i)} className="absolute -top-1.5 -right-1.5 bg-white border border-nardo-200 rounded-full p-0.5 text-nardo-400 hover:text-red-600 shadow-sm"><X className="w-3 h-3" /></button>
                <div className="text-[9px] text-nardo-500 max-w-[5rem] truncate mt-0.5">{f.name}</div>
              </div>
            ) : (
              <span key={i} className="text-[10px] inline-flex items-center gap-1 bg-nardo-100 text-navy rounded-md px-2 py-1" data-testid={`${base}-file-${i}`}>
                <a href={url} target="_blank" rel="noreferrer" className="hover:underline max-w-[8rem] truncate inline-flex items-center gap-1"><FileText className="w-3 h-3 shrink-0" />{f.name}</a>
                <button type="button" onClick={() => removeAt(i)} className="text-nardo-400 hover:text-red-600"><X className="w-3 h-3" /></button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
